/**
 * The Luminous Advocacy Project — interactive field-work filmstrip (#mission).
 *
 * Loads every image in dev/images/advocacy/ (the dev server lists that folder at
 * /api/advocacy-images) and lays them out as a seamless horizontal marquee. Drop
 * any number of photos into the folder and refresh — they all appear, and the
 * pace stays constant regardless of count.
 *
 * Motion is driven in JS (not CSS) so it can be touched:
 *   - Auto-scrolls continuously by default (leftward, calm pace).
 *   - Press and HOLD (or hover with a mouse) to pause; let go and it resumes.
 *   - DRAG left / right to scrub back and forth through the photos.
 *   - FLICK to fling — momentum carries you fast, then eases back to auto-scroll.
 * The track holds two identical runs of the slides; we translate by exactly one
 * run's width and wrap, so the loop is seamless in both directions.
 *
 * prefers-reduced-motion: no auto-motion and no clone — a plain, natively
 * scrollable strip (CSS handles overflow-x). Empty folder: soft placeholder
 * tiles + a hint, so the layout is visible now.
 *
 * Shopify port: swap the fetch for a Liquid loop over section image blocks; the
 * marquee markup, CSS and this interaction code stay as-is.
 */
(function () {
  var root = document.querySelector('[data-advocacy-marquee]');
  if (!root) return;
  var track = root.querySelector('[data-marquee-track]');
  if (!track) return;

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var SECONDS_PER_SLIDE = 4.5; // auto-scroll pace: one slide-width every 4.5s
  var FRICTION = 0.94; // fling velocity decay, per 60fps frame
  var MAX_FLING = 3.2; // px/ms cap on fling speed
  var DRAG_THRESHOLD = 6; // px of travel before a press becomes a scrub

  function photoSlide(url) {
    var fig = document.createElement('figure');
    fig.className = 'marquee__slide';
    var img = document.createElement('img');
    img.src = url;
    img.alt = 'The Luminous Advocacy Project in the field';
    img.decoding = 'async';
    img.draggable = false;
    fig.appendChild(img);
    return fig;
  }

  function placeholderSlide(label) {
    var fig = document.createElement('figure');
    fig.className = 'marquee__slide marquee__slide--placeholder';
    fig.textContent = label;
    return fig;
  }

  function build(urls) {
    var usingPlaceholders = !urls || !urls.length;
    var items = usingPlaceholders
      ? ['Field work', 'A device placed', 'Training day', 'On the ground', 'One given', 'In her hands']
      : urls;
    var makeOne = usingPlaceholders ? placeholderSlide : photoSlide;
    var runCount = items.length;

    // First run.
    items.forEach(function (it) {
      track.appendChild(makeOne(it));
    });

    if (usingPlaceholders) {
      var hint = document.createElement('p');
      hint.className = 'marquee__hint';
      hint.textContent =
        'Add photos to this section (Advocacy photo blocks) to fill the filmstrip.';
      root.insertAdjacentElement('afterend', hint);
    }

    // Reduced motion: no clone, no scripted motion — CSS gives a scrollable strip.
    if (reduce) return;

    // Second run, for the seamless loop.
    items.forEach(function (it) {
      var clone = makeOne(it);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    drive(runCount);
  }

  // ---- Scripted motion: auto-scroll + hold-to-pause + drag-scrub + fling ----
  function drive(runCount) {
    root.classList.add('marquee--interactive');

    // The first slide of the second run; its offset from the start is exactly
    // one loop's width. Recomputed on load/resize since image widths settle late.
    var loopMarker = track.children[runCount];
    var loopWidth = 0;
    var autoSpeed = 0; // px/ms, derived so pace is constant across any image set

    function recalc() {
      loopWidth = loopMarker ? loopMarker.offsetLeft : 0;
      autoSpeed = loopWidth > 0 ? loopWidth / (runCount * SECONDS_PER_SLIDE * 1000) : 0;
      offset = wrap(offset);
      apply();
    }

    var offset = 0; // px the track is shifted left; kept within [0, loopWidth)
    var mode = 'auto'; // 'auto' | 'hold' | 'drag' | 'fling'
    var flingVel = 0; // px/ms during a fling
    var hovering = false; // mouse hover pause (fine pointers only)

    // Drag bookkeeping.
    var activeId = null;
    var startX = 0;
    var startOffset = 0;
    var lastX = 0;
    var lastT = 0;
    var dragVel = 0; // smoothed px/ms of offset change while dragging
    var moved = false;

    function wrap(v) {
      if (loopWidth <= 0) return 0;
      v = v % loopWidth;
      return v < 0 ? v + loopWidth : v;
    }

    function apply() {
      track.style.transform = 'translateX(' + -offset + 'px)';
    }

    var lastFrame = null;
    function frame(t) {
      if (lastFrame === null) lastFrame = t;
      var dt = t - lastFrame;
      lastFrame = t;
      if (dt > 64) dt = 64; // clamp after a tab-switch / long stall

      if (mode === 'auto') {
        if (!hovering && autoSpeed > 0) {
          offset = wrap(offset + autoSpeed * dt);
          apply();
        }
      } else if (mode === 'fling') {
        offset = wrap(offset + flingVel * dt);
        apply();
        flingVel *= Math.pow(FRICTION, dt / (1000 / 60));
        if (Math.abs(flingVel) <= autoSpeed) mode = 'auto';
      }
      // 'hold' is paused; 'drag' is applied live in onMove.
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    function onDown(e) {
      if (activeId !== null) return;
      activeId = e.pointerId;
      try {
        track.setPointerCapture(e.pointerId);
      } catch (_) {}
      mode = 'hold';
      moved = false;
      startX = lastX = e.clientX;
      startOffset = offset;
      lastT = e.timeStamp;
      dragVel = 0;
    }

    function onMove(e) {
      if (e.pointerId !== activeId) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        mode = 'drag';
      }
      if (mode !== 'drag') return;
      // Drag right → reveal earlier photos (offset shrinks); drag left → later.
      offset = wrap(startOffset - dx);
      apply();
      var dt = e.timeStamp - lastT;
      if (dt > 0) {
        var instV = -(e.clientX - lastX) / dt; // offset velocity, px/ms
        dragVel = dragVel * 0.7 + instV * 0.3; // light smoothing
        lastT = e.timeStamp;
        lastX = e.clientX;
      }
      e.preventDefault();
    }

    function onUp(e) {
      if (e.pointerId !== activeId) return;
      try {
        track.releasePointerCapture(e.pointerId);
      } catch (_) {}
      activeId = null;
      if (moved && Math.abs(dragVel) > autoSpeed) {
        flingVel = Math.max(-MAX_FLING, Math.min(MAX_FLING, dragVel));
        mode = 'fling';
      } else {
        mode = 'auto'; // a hold/tap, or a slow drag → just resume
      }
    }

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove, { passive: false });
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);

    // Mouse hover pauses (matches the old affordance); touch/pen ignore this.
    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') hovering = true;
    });
    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') hovering = false;
    });

    // Loop width settles as images load and on resize.
    recalc();
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', recalc, { once: true });
    });
    window.addEventListener('load', recalc);
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recalc, 150);
    });
  }

  // Shopify: the section renders the image URLs (from image blocks, or an
  // asset-filename fallback) into a JSON <script data-advocacy-images> inside
  // the marquee root. No network fetch. Empty/missing → placeholder tiles.
  var urls = null;
  var data = root.querySelector('[data-advocacy-images]');
  if (data) {
    try {
      var parsed = JSON.parse(data.textContent || '[]');
      if (Array.isArray(parsed) && parsed.length) urls = parsed;
    } catch (e) {}
  }
  build(urls);
})();
